import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Row, Col, Form } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';

import { requestList as request, requestDownload } from 'api/sfms/report';
import ActionButton from 'components/common/ActionButton';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import Select from 'components/common-sfims/form/Select';
import ListInContent from 'components/common-sfims/ListInContent';
import SetMixPlan from 'components/pages/sfms/report/mix/plan/Setting';
import SetMixCompare from 'components/pages/sfms/report/mix/compare/Setting';
import SetAnalysisProduct from 'components/pages/sfms/report/analysis/product/Setting';
import SetAnalysisQuality from 'components/pages/sfms/report/analysis/quality/Setting';
import columns from 'components/pages/sfms/report/columns';
import SetIOMC from 'components/pages/sfms/report/iomc/modal/Setting';
import SetPricePrediction from 'components/pages/sfms/report/price/prediction/Setting';
import SetPriceProduct from 'components/pages/sfms/report/price/product/Setting';
import SetPriceRawMaterial from 'components/pages/sfms/report/price/rawMaterial/Setting';
import { array2ValueAndText, utc2tz } from 'function/convert';
import { onListClick } from 'function/event';
import { downloadFile } from 'function/file';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import useBUState from 'global/buState';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useListScroll from 'hooks/useListScroll';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import isEmpty from 'utils/isEmpty';
import comma from 'utils/format/comma';
import format from 'utils/string/format';

/**
 * 리포트 - 목록
 */
const List = () => {
  /* hook */
  const { end, setEnd, total, setTotal } = useListScroll();

  /* router */
  const history = useHistory();
  const { state } = useLocation();
  const { type } = useParams();

  /* recoil */
  const { bu } = useBUState();
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { koreas: koreaPlants }
  } = usePlantState();

  /* state */
  // 선택한 BU
  const [buCode, setBUCode] = useState(state?.params?.buCode);
  // 목록
  const [datas, setDatas] = useState([]);
  // 선택한 공장
  const [factoryCodeSeq, setFactoryCodeSeq] = useState(
    state?.params?.factoryCodeSeq
  );
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 배합비 시행 계획 리포트 생성 모달 표시 여부
  const [isShowSetMixPlan, setIsShowSetMixPlan] = useState(false);
  // 배합비 시행 계획 실적 비교 리포트 생성 모달 표시 여부
  const [isShowSetMixCompare, setIsShowSetMixCompare] = useState(false);
  // 생산성 분석 리포트 생성 모달 표시 여부
  const [isShowSetAnalysisProduct, setIsShowSetAnalysisProduct] =
    useState(false);
  // 품질 결과 분석 리포트 생성 모달 표시 여부
  const [isShowSetAnalysisQuality, setIsShowSetAnalysisQuality] =
    useState(false);
  // 장기 IOMC 예측 리포트 생성 모달 표시 여부
  const [isShowSetIOMC, setIsShowSetIOMC] = useState(false);
  // 장기 원재료비 예측 리포트 생성 모달 표시 여부
  const [isShowSetPricePrediction, setIsShowSetPricePrediction] =
    useState(false);
  // 공장별 축종 제품 원재료비 비교 리포트 생성 모달 표시 여부
  const [isShowSetPriceProduct, setIsShowSetPriceProduct] = useState(false);
  // 공장별 원료 가격 비교 리포트 생성 모달 표시 여부
  const [isShowSetPriceRawMaterial, setIsShowSetPriceRawMaterial] =
    useState(false);
  // 컬럼 목록
  const [listColumns, setListColumns] = useState([]);
  // 메시지
  const [message, setMessage] = useState({});
  // 리포트 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer(state?.params ?? {});
  // 선택한 월
  const [reportYm, setReportYm] = useState(params.reportYm);

  /* watch */
  useEffect(() => {
    let listColumns = Object.entries(columns);

    // 데이터 초기화
    setBUCode();
    setFactoryCodeSeq();

    // 컬럼 목록 설정
    switch (Math.floor(type)) {
      // BU별
      case 1:
      case 4:
      case 6:
        listColumns = listColumns.filter(([key]) => key !== 'pltName');

        break;

      // 공장별
      // case 2:
      case 7:
      case 8:
        listColumns = listColumns.filter(([key]) => key !== 'buName');

        break;

      // 전사
      default:
        listColumns = listColumns.filter(
          ([key]) => !['buName', 'pltName'].includes(key)
        );
    }

    // 장기 IOMC 예측 리포트가 아닌 경우 수정일을 표시하지 않음
    if (Math.floor(type) !== 6) {
      listColumns = listColumns.filter(([key]) => key !== 'updDt');
    }

    setListColumns(Object.fromEntries(listColumns));
    setParams({ reportType: type });
  }, [type]);
  useEffect(() => {
    if (!isEmpty(params)) {
      requestList();
    }
  }, [params]);

  /* method */
  // 리포트 다운로드 요청
  const requestDownloadReport = async seq => {
    try {
      const { data, name } = await requestDownload(seq);

      downloadFile(data, name);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    }
  };
  // 리포트 목록 조회 요청
  const requestList = async () => {
    setEnd(20);
    setTotal(0);
    setDatas([]);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setTotal(totalCount);
      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: totalCount - index,
          reportYmText: moment(value.reportYm).format(
            format(getText('ym'), {
              year: 'YYYY',
              month: 'MM'
            })
          ),
          regDt: utc2tz(value.regDt),
          updDt: utc2tz(value.updDt)
        }))
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 설정 모달 표시
  const showSetting = () => {
    const setIsShowSetting = {
      1: setIsShowSetMixPlan,
      2: setIsShowSetMixCompare,
      3: setIsShowSetPriceRawMaterial,
      4: setIsShowSetPriceProduct,
      5: setIsShowSetPricePrediction,
      6: setIsShowSetIOMC,
      7: setIsShowSetAnalysisProduct,
      8: setIsShowSetAnalysisQuality
    };

    setIsShowSetting[type](true);
  };
  // 장기 IOMC 예측 리포트 생성 화면 표시
  const showSetIOMC = reportData => {
    const state = { params };

    if (reportData.seq) {
      state.data = { viewType: 'update' };
    }

    history.push(
      format(
        ROUTER_PATH.sfms.report.iomc[reportData.seq ? 'inform' : 'setting'],
        {
          ...reportData,
          type
        }
      ),
      state
    );
  };

  /* event */
  // 목록 클릭 시
  const onClick = ({
    reportSubType,
    reportYm,
    buCode,
    factoryCodeSeq,
    seq
  }) => {
    switch (Math.floor(type)) {
      // 장기 IOMC 예측
      case 6:
        history.push(
          format(ROUTER_PATH.sfms.report.iomc.inform, {
            type,
            iomcYm: reportYm,
            buCode,
            seq
          }),
          { params }
        );

        break;

      // 품질 결과 분석
      case 8:
        history.push(
          format(
            ROUTER_PATH.sfms.report.analysis.quality[
              ['rawMaterial', 'product'][reportSubType]
            ],
            {
              type,
              reportYm,
              factoryCodeSeq,
              seq
            }
          ),
          { params }
        );

        break;
    }
  };
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      reportYm,
      buCode,
      factoryCodeSeq
    });
  };

  /* view */
  return (
    <>
      <ListInContent
        columns={Object.entries(listColumns).map(
          ([key, { title } = {}]) => title ?? key
        )}
        message={message}
        showLoading={isLoading}
      >
        <Fragment key="headerButton">
          <Button size="sm" className="ms-2" onClick={showSetting}>
            {getText('insertReport')}
          </Button>
        </Fragment>
        <Fragment key="search">
          <Form onSubmit={onSearchClick}>
            <Row className="ms-0 me-0 mt-3">
              <Col sm={2} className="ps-1 pe-1">
                <MonthPicker
                  value={reportYm}
                  placeholderText={getText('selectYm')}
                  onUpdate={setReportYm}
                />
              </Col>

              {/*
                BU 선택 표시
                - 배합비 시행 계획
                - 공장별 축종 제품 원재료비 비교
                - 장기 IOMC 예측
               */}
              {[1, 4, 6].includes(Math.floor(type)) && (
                <Col sm={2} className="ps-1 pe-1">
                  <Select
                    datas={array2ValueAndText(bu, 'buCode', 'buName')}
                    placeholder="selectBU"
                    showAll
                    value={buCode}
                    className="w-100"
                    onChange={({ target: { value } }) => setBUCode(value)}
                  />
                </Col>
              )}

              {/*
                공장 선택 표시
                - 생산성 분석
                - 품질 결과 분석
               */}
              {[7, 8].includes(Math.floor(type)) && (
                <Col sm={2} className="ps-1 pe-1">
                  <Select
                    datas={array2ValueAndText(koreaPlants, 'seq', 'pltNm')}
                    placeholder="selectFactory"
                    showAll
                    value={factoryCodeSeq}
                    className="w-100"
                    onChange={({ target: { value } }) => {
                      setFactoryCodeSeq(value);
                    }}
                  />
                </Col>
              )}

              <Col sm="auto" className="ps-1 pe-1 d-flex align-items-center">
                <Button disabled={isLoading} size="sm" type="submit">
                  {getText('search')}
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  {format(getText('total'), { total: comma(total) })}
                </p>
              </Col>
            </Row>
          </Form>
        </Fragment>
        <Fragment key="datas">
          {datas.slice(0, end).map((value = {}) => (
            <tr
              key={`datas${value.rowNum}`}
              onClick={event => onListClick(event, () => onClick(value))}
            >
              {Object.keys(listColumns).map(key => (
                <td
                  key={`datas${value.rowNum}.${key}`}
                  className={classNames({
                    'align-middle': key === 'download',
                    'cursor-pointer':
                      [6, 8].includes(Math.floor(type)) && key !== 'download'
                  })}
                >
                  {key === 'download' ? (
                    <ActionButton
                      title={getText(key)}
                      icon="paperclip"
                      variant="action"
                      className="p-0 lh-1"
                      onClick={() => requestDownloadReport(value.seq)}
                    />
                  ) : (
                    value[key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </Fragment>
      </ListInContent>
      <SetMixPlan
        show={isShowSetMixPlan}
        onHide={() => setIsShowSetMixPlan(false)}
        onSetting={requestList}
      />
      <SetMixCompare
        show={isShowSetMixCompare}
        onHide={() => setIsShowSetMixCompare(false)}
        onSetting={requestList}
      />
      <SetPriceRawMaterial
        show={isShowSetPriceRawMaterial}
        onHide={() => setIsShowSetPriceRawMaterial(false)}
        onSetting={requestList}
      />
      <SetPriceProduct
        show={isShowSetPriceProduct}
        onHide={() => setIsShowSetPriceProduct(false)}
        onSetting={requestList}
      />
      <SetPricePrediction
        show={isShowSetPricePrediction}
        onHide={() => setIsShowSetPricePrediction(false)}
        onSetting={requestList}
      />
      <SetIOMC
        show={isShowSetIOMC}
        onHide={() => setIsShowSetIOMC(false)}
        onSetting={showSetIOMC}
      />
      <SetAnalysisProduct
        show={isShowSetAnalysisProduct}
        onHide={() => setIsShowSetAnalysisProduct(false)}
        onSetting={requestList}
      />
      <SetAnalysisQuality
        show={isShowSetAnalysisQuality}
        onHide={() => setIsShowSetAnalysisQuality(false)}
        onSetting={requestList}
      />
    </>
  );
};

export default List;
